import { useEffect, useRef, useState } from 'react';
import { MAX_CHUNKS, MAX_CHUNK_DELAY_MS, MAX_DELAY_MS, PAYLOAD_PARAM_OPTIONS } from '@xsscool/shared';
import { useStore } from '../store';

function clampInt(raw: string, min: number, max: number, fallback: number): number {
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

// Status / delay / body type live next to the tabs so the sidebar can be
// just the payload library. The drip (chunks) controls sit in a popover
// because they are rarely used.
export function ResponseControls() {
  const status = useStore((s) => s.status);
  const delay = useStore((s) => s.delay);
  const chunks = useStore((s) => s.chunks);
  const chunkDelay = useStore((s) => s.chunkDelay);
  const payloadParam = useStore((s) => s.payloadParam);
  const setStatus = useStore((s) => s.setStatus);
  const setDelay = useStore((s) => s.setDelay);
  const setChunks = useStore((s) => s.setChunks);
  const setChunkDelay = useStore((s) => s.setChunkDelay);
  const setPayloadParam = useStore((s) => s.setPayloadParam);
  const [dripOpen, setDripOpen] = useState(false);
  const [dripPos, setDripPos] = useState<{ top: number; left: number } | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);
  const dripBtn = useRef<HTMLButtonElement | null>(null);

  // The tab strip scrolls horizontally, which clips absolutely positioned
  // children. Position the popover fixed, below the button, instead.
  const openDrip = () => {
    const r = dripBtn.current?.getBoundingClientRect();
    if (r) setDripPos({ top: r.bottom + 6, left: Math.max(8, r.right - 260) });
    setDripOpen((v) => !v);
  };

  useEffect(() => {
    if (!dripOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setDripOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [dripOpen]);

  const dripActive = chunks > 1 && chunkDelay > 0;

  return (
    <div className="response-controls">
      <select
        className="body-type"
        value={payloadParam}
        onChange={(e) => setPayloadParam(e.target.value as typeof payloadParam)}
        title="Body type: which query param the body maps to, and the Content-Type"
        aria-label="Body type"
      >
        {PAYLOAD_PARAM_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label} ({o.paramLabel})
          </option>
        ))}
      </select>
      <label className="field inline" title="HTTP status code (200–599)">
        <span>Status Code</span>
        <input
          type="number"
          min={200}
          max={599}
          value={status}
          onChange={(e) => setStatus(clampInt(e.target.value, 200, 599, 200))}
        />
      </label>
      <label className="field inline" title="Delay before the response starts, in ms">
        <span>Delay</span>
        <input
          type="number"
          min={0}
          max={MAX_DELAY_MS}
          value={delay}
          onChange={(e) => setDelay(clampInt(e.target.value, 0, MAX_DELAY_MS, 0))}
        />
        <span className="unit">ms</span>
      </label>
      <div className="menu-wrap" ref={ref}>
        <button
          type="button"
          ref={dripBtn}
          className={'drip-toggle' + (dripActive ? ' active' : '')}
          onClick={openDrip}
          aria-expanded={dripOpen}
          title="Drip: send the body in several chunks with a pause between them"
        >
          {dripActive ? `Drip ${chunks}×${chunkDelay}ms` : 'Drip'}
        </button>
        {dripOpen && (
          <div
            className="drip-popover"
            role="dialog"
            aria-label="Drip settings"
            style={dripPos ? { position: 'fixed', top: dripPos.top, left: dripPos.left, right: 'auto' } : undefined}
          >
            <label className="field inline">
              <span>Chunks</span>
              <input
                type="number"
                min={1}
                max={MAX_CHUNKS}
                value={chunks}
                onChange={(e) => setChunks(clampInt(e.target.value, 1, MAX_CHUNKS, 1))}
              />
            </label>
            <label className="field inline">
              <span>Pause</span>
              <input
                type="number"
                min={0}
                max={MAX_CHUNK_DELAY_MS}
                value={chunkDelay}
                onChange={(e) => setChunkDelay(clampInt(e.target.value, 0, MAX_CHUNK_DELAY_MS, 0))}
              />
              <span className="unit">ms</span>
            </label>
            <p className="hint">Body is split into N pieces sent with a pause between them (chunked encoding). Both must be set.</p>
          </div>
        )}
      </div>
    </div>
  );
}
